import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { makeCacheKey, readCache, writeCache } from '../lib/productCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
  belts: { title: 'Belts', description: 'Premium leather belts for men, women, and children', subcategories: ['men', 'women', 'children'] },
  wallets: { title: 'Wallets', description: 'Handcrafted leather wallets and cardholders', subcategories: [] },
  purses: { title: 'Purses', description: 'Elegant leather purses and handbags for women', subcategories: [] },
  dog_collars: { title: 'Dog Collars', description: 'Luxury leather collars for your furry friends', subcategories: [] },
  cologne: { title: 'Cologne', description: 'Sophisticated fragrances for the modern man', subcategories: [] },
};

export default function ShopPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sort, setSort] = useState('newest');
  const [subcategory, setSubcategory] = useState(searchParams.get('sub') || '');

  const info = categoryInfo[category] || { title: 'All Products', description: 'Browse our complete collection', subcategories: [] };

  useEffect(() => {
    const fetchProducts = async () => {
      const params = { limit: 50, sort };
      if (category) params.category = category;
      if (subcategory) params.subcategory = subcategory;
      if (priceRange[0] > 0) params.min_price = priceRange[0];
      if (priceRange[1] < 500) params.max_price = priceRange[1];
      const cacheKey = makeCacheKey('products', params);
      const cached = readCache(cacheKey);

      if (cached) {
        setProducts(Array.isArray(cached) ? cached : []);
        setLoading(false);
        setError(null);
      }

      try {
        if (!cached) {
          setLoading(true);
        }
        setError(null);
        const response = await axios.get(`${API}/products`, { params, timeout: 15000 });
        setProducts(Array.isArray(response.data) ? response.data : []);
        writeCache(cacheKey, Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching products:', error);
        if (!cached) {
          setError(error.message || 'Failed to load products');
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, subcategory, priceRange, sort]);

  const handleSubcategoryChange = (value) => {
    setSubcategory(value === 'all' ? '' : value);
    if (value === 'all') {
      searchParams.delete('sub');
    } else {
      searchParams.set('sub', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen" data-testid="shop-page">
      {/* Hero Banner */}
      <section className="relative h-64 md:h-80 flex items-center justify-center bg-leather-900" data-testid="shop-hero">
        <div className="absolute inset-0 opacity-30">
          <img
            src={category === 'purses' ? '/images/purses/Brown%20Leather%20Purse.jpg' : category === 'belts' ? 'https://images.unsplash.com/photo-1664286074240-d7059e004dff?w=1920&q=80' : category === 'wallets' ? 'https://images.unsplash.com/photo-1620109176813-e91290f6c795?w=1920&q=80' : category === 'cologne' ? 'https://images.unsplash.com/photo-1594036049305-007afaf640a4?w=1920&q=80' : category === 'dog_collars' ? 'https://images.unsplash.com/photo-1582046989902-5c7bcc2eb366?w=1920&q=80' : 'https://images.unsplash.com/photo-1582046989902-5c7bcc2eb366?w=1920&q=80'}
            alt={info.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-6">
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">{info.title}</h1>
          <p className="text-cream-100/80 max-w-lg mx-auto">{info.description}</p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0" data-testid="filters-sidebar">
            <div className="sticky top-24 space-y-8">
              {/* Subcategory Filter */}
              {info.subcategories.length > 0 && (
                <div className="filter-section">
                  <h3 className="font-accent text-xs tracking-widest uppercase text-leather-900 mb-4">Category</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSubcategoryChange('all')}
                      className={`category-pill ${!subcategory ? 'active' : ''}`}
                      data-testid="filter-all"
                    >
                      All
                    </button>
                    {info.subcategories.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => handleSubcategoryChange(sub)}
                        className={`category-pill ${subcategory === sub ? 'active' : ''}`}
                        data-testid={`filter-${sub}`}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Filter */}
              <div className="filter-section">
                <h3 className="font-accent text-xs tracking-widest uppercase text-leather-900 mb-4">Price Range</h3>
                <div className="px-1">
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={500}
                    step={10}
                    className="mb-4"
                    data-testid="price-slider"
                  />
                  <div className="flex justify-between text-sm text-leather-600">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                </div>
              </div>

              {/* Sort */}
              <div className="filter-section border-b-0">
                <h3 className="font-accent text-xs tracking-widest uppercase text-leather-900 mb-4">Sort By</h3>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-full bg-transparent border-cream-200" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-cream-50">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-8">
              <p className="text-leather-600 text-sm">
                {products.length} {products.length === 1 ? 'product' : 'products'}
              </p>
            </div>

            

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="spinner"></div>
              </div>
            ) : error ? (
              <div className="text-center py-16 px-6" data-testid="error-state">
                <div className="max-w-md mx-auto bg-cream-100 border-2 border-gold-500 rounded-lg p-8">
                  <h3 className="text-leather-900 text-xl font-heading mb-4">Unable to Load Products</h3>
                  <p className="text-leather-600 mb-4">The backend server is not responding. This could mean:</p>
                  <ul className="text-left text-leather-600 text-sm space-y-2 mb-6">
                    <li>• Backend is not deployed or running</li>
                    <li>• Database connection is not configured</li>
                    <li>• CORS settings need adjustment</li>
                  </ul>
                  <div className="bg-white p-4 rounded border border-cream-300 text-left">
                    <p className="text-xs font-mono text-leather-500 mb-2">Backend URL:</p>
                    <p className="text-xs font-mono text-leather-800 break-all">{process.env.REACT_APP_BACKEND_URL || 'Not configured'}</p>
                  </div>
                </div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 px-6" data-testid="no-products">
                <div className="max-w-md mx-auto">
                  <h3 className="text-leather-900 text-xl font-heading mb-4">No Products Available</h3>
                  <p className="text-leather-600 mb-4">The database appears to be empty.</p>
                  <div className="bg-cream-100 border border-cream-300 rounded-lg p-6 text-left">
                    <p className="text-sm text-leather-700 mb-3 font-semibold">To add products:</p>
                    <ol className="text-sm text-leather-600 space-y-2 list-decimal list-inside">
                      <li>Ensure backend is running with MongoDB connected</li>
                      <li>Visit the backend URL and call the /api/seed endpoint</li>
                      <li>Or use the Admin page to manually add products</li>
                    </ol>
                  </div>
                  {!category && (
                    <p className="text-leather-400 text-sm mt-4">Try adjusting your filters</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
